import { ApiClient } from "../lib/api/baseClient";

export interface SendVerificationResponse {
  ok: boolean;
  link?: string | null;
}

// Create client for email verification
const verifyClient = new ApiClient();

/**
 * Send verification email for authenticated user.
 * Called during registration flow when user has a valid token.
 */
export async function callSendEmailVerification(
  _apiBaseUrl: string,
  token: string,
): Promise<SendVerificationResponse> {
  verifyClient.setAuthToken(token);
  const result = await verifyClient.post<SendVerificationResponse>(
    "/auth/email-verification/send",
    {},
    { defaultErrorCode: "VERIFY_EMAIL_SEND_FAILED" },
  );

  // Mark that we're awaiting verification so VerifyEmailPage can
  // distinguish between "came from email redirect" vs "navigated directly"
  // Use localStorage (not sessionStorage) because email links open in new tabs
  if (result.ok) {
    localStorage.setItem("awaitingVerification", "true");
  }

  return result;
}

export interface ResendVerificationResponse {
  ok: boolean;
  message?: string;
}

/**
 * Resend verification email by email address.
 * Called when a user needs to request a new verification link
 * (e.g., after the original link expired).
 *
 * Note: This is an unauthenticated endpoint since the user cannot log in
 * without verifying their email first.
 */
export async function callResendEmailVerification(
  _apiBaseUrl: string,
  email: string,
): Promise<ResendVerificationResponse> {
  // Use a fresh client without auth token for this unauthenticated endpoint
  const unauthClient = new ApiClient();
  const result = await unauthClient.post<ResendVerificationResponse>(
    "/auth/email-verification/resend",
    { email },
    { defaultErrorCode: "RESEND_EMAIL_FAILED" },
  );

  // Mark that we're awaiting verification (same as initial send)
  // Use localStorage (not sessionStorage) because email links open in new tabs
  if (result.ok) {
    localStorage.setItem("awaitingVerification", "true");
  }

  return result;
}
